import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, Send, Plus } from 'lucide-react';
import { useMenuItems } from '../../../api/queries/menu.queries';
import { useCreateRecipe, useUpdateRecipe, useSubmitRecipeForReview, useCloneRecipe } from '../../../api/mutations/recipe.mutations';
import FormulaEditor from './FormulaEditor';
import type { Recipe, Formula } from '../../../api/queries/recipe.queries';

interface RecipeEditModalProps {
  recipe?: Recipe;
  onClose: () => void;
  onSuccess: () => void;
}

const SIZES = ['small', 'medium', 'large', 'all'] as const;

function RecipeEditModal({ recipe, onClose, onSuccess }: RecipeEditModalProps) {
  const { i18n } = useTranslation();
  const { data: menuItems } = useMenuItems();
  const createMutation = useCreateRecipe();
  const updateMutation = useUpdateRecipe(recipe?._id || '');
  const submitMutation = useSubmitRecipeForReview(recipe?._id || '');
  const cloneMutation = useCloneRecipe(recipe?._id || '');

  const getMenuItemId = (menuItemId: string | { _id: string; name: { en: string; th: string }; sku: string } | undefined) => {
    if (!menuItemId) return '';
    if (typeof menuItemId === 'object') return menuItemId._id;
    return menuItemId;
  };

  const [menuItemId, setMenuItemId] = useState(getMenuItemId(recipe?.menuItemId));
  const [size, setSize] = useState<typeof SIZES[number]>(recipe?.size || 'all');
  const [autoSwitchEnabled, setAutoSwitchEnabled] = useState(recipe?.autoSwitchEnabled ?? true);
  const [formulas, setFormulas] = useState<Formula[]>(
    recipe?.formulas || [
      {
        _id: crypto.randomUUID(),
        formulaCode: 'A',
        name: 'Primary Formula',
        ingredients: [],
        isDefault: true,
        priority: 1,
      },
    ]
  );
  const [notes, setNotes] = useState(recipe?.notes || '');
  const [errors, setErrors] = useState<{ menuItemId?: string }>({});

  useEffect(() => {
    if (recipe) {
      setMenuItemId(getMenuItemId(recipe.menuItemId));
      setSize(recipe.size);
      setAutoSwitchEnabled(recipe.autoSwitchEnabled);
      setFormulas(recipe.formulas);
      setNotes(recipe.notes || '');
    } else {
      // Reset form for create mode
      setMenuItemId('');
      setSize('all');
      setAutoSwitchEnabled(true);
      setFormulas([
        {
          _id: crypto.randomUUID(),
          formulaCode: 'A',
          name: 'Primary Formula',
          ingredients: [],
          isDefault: true,
          priority: 1,
        },
      ]);
      setNotes('');
      setErrors({});
    }
  }, [recipe]);

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const validate = () => {
    const newErrors: { menuItemId?: string } = {};

    if (!menuItemId) {
      newErrors.menuItemId = 'Menu item is required';
    }

    if (formulas.length === 0) {
      alert('At least one formula is required');
      return false;
    }

    const hasDefault = formulas.some(f => f.isDefault);
    if (!hasDefault) {
      alert('Please set a default formula');
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormulaChange = (index: number, updated: Formula) => {
    setFormulas((prev) => prev.map((f, i) => (i === index ? updated : f)));
  };

  const handleSetDefault = (index: number) => {
    setFormulas((prev) =>
      prev.map((f, i) => ({
        ...f,
        isDefault: i === index,
        priority: i === index ? 1 : f.priority,
      }))
    );
  };

  const handleDeleteFormula = (index: number) => {
    if (formulas.length <= 1) return;
    const isDefault = formulas[index].isDefault;
    setFormulas((prev) => {
      const newFormulas = prev.filter((_, i) => i !== index);
      // If we deleted the default, set first one as default
      if (isDefault && newFormulas.length > 0) {
        newFormulas[0].isDefault = true;
      }
      return newFormulas;
    });
  };

  const handleAddFormula = () => {
    const nextCode = String.fromCharCode(65 + formulas.length); // A, B, C, D...
    setFormulas((prev) => [
      ...prev,
      {
        _id: crypto.randomUUID(),
        formulaCode: nextCode,
        name: `Formula ${nextCode}`,
        ingredients: [],
        isDefault: false,
        priority: prev.length + 1,
      },
    ]);
  };

  const handleCopyFormula = (index: number) => {
    const formulaToCopy = formulas[index];
    const nextCode = String.fromCharCode(65 + formulas.length); // A, B, C, D...
    setFormulas((prev) => [
      ...prev,
      {
        _id: crypto.randomUUID(),
        formulaCode: nextCode,
        name: `${formulaToCopy.name} (Copy)`,
        description: formulaToCopy.description,
        ingredients: formulaToCopy.ingredients.map(ing => ({ ...ing })),
        isDefault: false,
        priority: prev.length + 1,
        useCase: formulaToCopy.useCase,
      },
    ]);
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const payload = {
        menuItemId,
        size,
        formulas: formulas.map(f => ({
          formulaCode: f.formulaCode,
          name: f.name,
          description: f.description,
          ingredients: f.ingredients.map(i => ({
            ingredientId: typeof i.ingredientId === 'object' ? i.ingredientId._id : i.ingredientId,
            quantity: i.quantity,
            unit: i.unit,
          })),
          isDefault: f.isDefault,
          priority: f.priority,
          useCase: f.useCase,
        })),
        autoSwitchEnabled,
        notes,
      };

      if (recipe) {
        // For published/review recipes, clone first then update the new draft
        if (recipe.status === 'published' || recipe.status === 'review') {
          const currentVersion = parseFloat(recipe.version) || 1;
          const newVersion = (currentVersion + 0.1).toFixed(1);

          // Clone to create draft, then the user can edit that
          await cloneMutation.mutateAsync({
            version: newVersion,
            notes: 'Created from edit',
          });
          // The clone creates a new draft with the existing data
          // User will need to open the new draft to make their changes
          alert(`Created new draft version ${newVersion}. Please find and edit the new draft.`);
        } else {
          // Draft recipe - update directly
          await updateMutation.mutateAsync(payload as any);
        }
      } else {
        await createMutation.mutateAsync(payload as any);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save recipe:', error);
    }
  };

  const handleSubmitForReview = async () => {
    if (!recipe) return;
    if (!confirm('Submit this recipe for review?')) return;

    try {
      await submitMutation.mutateAsync();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to submit recipe:', error);
    }
  };

  const isLoading =
    createMutation.isPending || updateMutation.isPending || submitMutation.isPending || cloneMutation.isPending;
  const isDraft = recipe?.status === 'draft' || !recipe;
  const isPublished = recipe?.status === 'published' || recipe?.status === 'review';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-lg font-bold">
              {!recipe ? 'Create Recipe' : 'Edit Recipe'}
            </h2>
            {recipe && (
              <div className="text-xs text-muted-foreground mt-1">
                v{recipe.version} •{' '}
                <span
                  className={`${
                    recipe.status === 'published'
                      ? 'text-green-600'
                      : recipe.status === 'review'
                      ? 'text-orange-600'
                      : 'text-gray-600'
                  }`}
                >
                  {recipe.status}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Menu Item & Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Menu Item <span className="text-destructive">*</span>
              </label>
              <select
                value={menuItemId}
                onChange={(e) => setMenuItemId(e.target.value)}
                disabled={!!recipe}
                className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                  errors.menuItemId ? 'border-destructive' : ''
                }`}
              >
                <option value="">Select menu item...</option>
                {menuItems?.map((item) => (
                  <option key={item._id} value={item._id}>
                    {getLangValue(item.name)}
                  </option>
                ))}
              </select>
              {errors.menuItemId && (
                <p className="text-xs text-destructive mt-1">{errors.menuItemId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Size</label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value as typeof SIZES[number])}
                disabled={!!recipe}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              >
                {SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s === 'all' ? 'All Sizes' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Auto-Switch Toggle */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <input
              type="checkbox"
              id="autoSwitch"
              checked={autoSwitchEnabled}
              onChange={(e) => setAutoSwitchEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="autoSwitch" className="text-sm">
              <span className="font-medium">Enable Auto-Switch</span>
              <p className="text-muted-foreground text-xs">
                Automatically switch to alternative formula when ingredients are out of stock
              </p>
            </label>
          </div>

          {/* Formulas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Formulas</label>
              <button
                onClick={handleAddFormula}
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <Plus className="h-4 w-4" />
                Add Formula
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {formulas.map((formula, index) => (
                <FormulaEditor
                  key={formula._id || index}
                  formula={formula}
                  onChange={(updated) => handleFormulaChange(index, updated)}
                  onSetDefault={() => handleSetDefault(index)}
                  onDelete={() => handleDeleteFormula(index)}
                  onCopy={() => handleCopyFormula(index)}
                  isOnlyFormula={formulas.length <= 1}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm min-h-[80px]"
              placeholder="Optional notes about this recipe..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t sticky bottom-0 bg-card">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg border bg-background px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              <Save className="h-4 w-4" />
              {isPublished ? 'Save as New Draft' : 'Save'}
            </button>

            {isDraft && recipe && (
              <button
                onClick={handleSubmitForReview}
                className="flex-1 rounded-lg bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={isLoading}
              >
                <Send className="h-4 w-4" />
                Submit for Review
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecipeEditModal;
